/**
 * Server → form sync rule for the profile editor.
 *
 * `currentUser` is rebuilt by spread in several reducers (`setUserMFA`,
 * `incrementUserTOTPCount`, `updateUser`), so its object identity changes when
 * nothing about the profile has. The editor's sync effect keys off that identity
 * and re-copies every field, which meant an absent server value was written over
 * whatever the form held.
 *
 * That is how a constituent's name, seeded from the organisation's roster during
 * sign-up, appeared and then vanished a moment later: the roster name only ever
 * lived in the form, and the MFA step's `setUserMFA` triggered a re-sync that
 * blanked it. A refresh made it reappear because no further identity change
 * followed the seed.
 *
 * Pure so it can be tested without mounting the editor and its dependency tree.
 */

/** A server value that carries no instruction — the record simply has no entry. */
export const isAbsent = (value: unknown): boolean => value === undefined || value === null || value === '';

/**
 * Whether the sync effect should copy `incoming` into the form field.
 *
 * The server having no opinion about a field is not an instruction to erase what
 * the form already holds, so an absent value never overwrites a populated field.
 */
export const shouldSyncField = (incoming: unknown, currentFormValue: unknown): boolean =>
	!(isAbsent(incoming) && !isAbsent(currentFormValue));
